/**
 * F10.1 Tracking core: PMAAnalytics.track(event_name, payload, options)
 * PRD-OPP-0015 FR-001: Single canonical event API.
 * - Common payload resolver (event_id, event_time, session_id, anonymous_id, page_*, member_state, etc.)
 * - Dedupe by dedupe_key or event_name+key
 * - Consent gate by consent_class (essential | functional | analytics | marketing)
 * - Dispatch: CustomEvent('pma:analytics', { detail }) + dataLayer.push
 * Ref: docs/analytics/f10.1_event_taxonomy_spec.md, PRD.v3 §10.2, PRD-OPP-0015.
 */
(function () {
  "use strict";

  var CONTEXT_KEY = "PMA_Analytics_Context";
  var ANON_ID_KEY = "pma_anonymous_id";
  var SESSION_ID_KEY = "pma_session_id";
  var DEDUPE_TTL_MS = 2000;
  var TRUNCATE_LEN = 100;
  var defaultConsentClass = "analytics";

  function isCanonicalEventName(eventName) {
    return typeof eventName === "string" && eventName.indexOf("pma_") === 0;
  }

  function getContext() {
    try {
      return typeof window[CONTEXT_KEY] === "object" ? window[CONTEXT_KEY] : {};
    } catch {
      return {};
    }
  }

  function uuidv4() {
    try {
      if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    } catch {}
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function isoNow() {
    return new Date().toISOString();
  }

  function getOrCreateId(key) {
    try {
      if (typeof localStorage === "undefined") return uuidv4();
      var id = localStorage.getItem(key);
      if (id) return id;
      id = uuidv4();
      localStorage.setItem(key, id);
      return id;
    } catch {
      return uuidv4();
    }
  }

  function getDeviceType() {
    try {
      var ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
      var w = typeof window !== "undefined" && window.innerWidth;
      if (/tablet|ipad/i.test(ua)) return "tablet";
      if (/mobile|android|iphone|ipod/i.test(ua) || (w && w < 768)) return "mobile";
      return "desktop";
    } catch {
      return "desktop";
    }
  }

  function getMemberState() {
    try {
      var body = typeof document !== "undefined" && document.body;
      if (!body) return "unknown";
      if (body.classList.contains("is-member-active")) return "member";
      // Common schema enum is intentionally coarse:
      // - member: active members
      // - non_member: all locked/pending/denied/past-due/suspended states
      if (body.classList.contains("is-member-locked")) return "non_member";
      if (body.classList.contains("is-member-pending-approval")) return "non_member";
      if (body.classList.contains("is-member-denied")) return "non_member";
      return "unknown";
    } catch {
      return "unknown";
    }
  }

  function getMembershipStateUi() {
    try {
      var body = typeof document !== "undefined" && document.body;
      if (!body) return "";
      // Theme-owned UI state (used by gating UX) is more detailed than common member_state.
      // Kept as an additional field for analytics segmentation without breaking the common schema enum.
      var v = body.getAttribute("data-pma-member-state") || "";
      return String(v || "").trim().slice(0, 32);
    } catch {
      return "";
    }
  }

  function getApprovalState() {
    try {
      var body = typeof document !== "undefined" && document.body;
      if (!body) return "unknown";
      if (body.classList.contains("is-member-pending-approval")) return "pending";
      if (body.classList.contains("is-member-denied")) return "rejected";
      if (body.classList.contains("is-member-active")) return "approved";
      return "not_required";
    } catch {
      return "unknown";
    }
  }

  function cleanUrlFromLocation(loc) {
    try {
      if (!loc || !loc.origin || !loc.pathname) return "";
      return String(loc.origin) + String(loc.pathname);
    } catch {
      return "";
    }
  }

  function cleanUrlString(url) {
    if (!url) return "";
    try {
      var u = new URL(String(url));
      return u.origin + u.pathname;
    } catch {
      return String(url).split("?")[0].split("#")[0];
    }
  }

  function resolveCommonPayload() {
    var ctx = getContext();
    var loc = typeof window !== "undefined" && window.location;
    return {
      event_name: "",
      event_version: 1,
      event_id: uuidv4(),
      event_time: isoNow(),
      session_id: getOrCreateId(SESSION_ID_KEY),
      anonymous_id: getOrCreateId(ANON_ID_KEY),
      shopify_shop_domain: ctx.shop_domain || (loc && loc.hostname) || "",
      page_url: cleanUrlFromLocation(loc) || "",
      page_path: (loc && loc.pathname) || "",
      page_referrer: (typeof document !== "undefined" && document.referrer) ? cleanUrlString(document.referrer) : null,
      page_type: ctx.page_type || "page",
      template: ctx.template || "",
      device_type: getDeviceType(),
      locale: ctx.locale || "",
      currency: ctx.currency || "",
      member_state: getMemberState(),
      // APA-UX-041 / GH #6813 ticket AC uses user_state; keep member_state as canonical F10.1 field.
      user_state: getMemberState(),
      membership_state: getMembershipStateUi() || undefined,
      approval_state: getApprovalState(),
      consent_state: ctx.consent_state || "unknown",
      release_version: (ctx.release_version && String(ctx.release_version).trim()) ? String(ctx.release_version).trim().slice(0, 64) : ""
    };
  }

  var dedupeMap = {};
  function isDedupeKeySeen(key) {
    var now = Date.now();
    if (dedupeMap[key] && now - dedupeMap[key] < DEDUPE_TTL_MS) return true;
    dedupeMap[key] = now;
    return false;
  }

  function allowUnknownConsent() {
    try {
      var ctx = getContext();
      if (ctx && ctx.analytics_debug_mode) return true;
      var loc = typeof window !== "undefined" && window.location;
      var q = (loc && loc.search) ? String(loc.search) : "";
      return q.indexOf("pma_debug_analytics=1") !== -1;
    } catch {
      return false;
    }
  }

  function isConsentGranted(consentClass, consentState) {
    if (consentClass === "essential") return true;
    if (consentState === "unknown") return allowUnknownConsent();
    if (consentClass === "analytics" && (consentState === "granted_analytics" || consentState === "granted_marketing")) return true;
    if (consentClass === "functional" && (consentState === "granted_functional" || consentState === "granted_analytics" || consentState === "granted_marketing")) return true;
    if (consentClass === "marketing" && consentState === "granted_marketing") return true;
    return false;
  }

  function truncate(str) {
    if (str == null) return "";
    var s = String(str).trim();
    return s.length > TRUNCATE_LEN ? s.slice(0, TRUNCATE_LEN) : s;
  }

  function sanitizePayload(payload) {
    if (payload == null || typeof payload !== "object") return {};
    var out = {};
    for (var key in payload) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
      var v = payload[key];
      // SEC-021: never transmit raw search terms. Send lengths/booleans instead.
      if (key === "query" || key === "term" || key === "normalized") continue;
      if (key === "link_text" || key === "context") out[key] = truncate(v);
      else out[key] = v;
    }
    return out;
  }

  function emitDispatchTelemetry(type, eventName, err) {
    try {
      document.dispatchEvent(new CustomEvent("pma:analytics:dispatch", {
        detail: { type: type, event_name: eventName, error: err ? String(err.message || err).slice(0, 200) : undefined },
        bubbles: true
      }));
    } catch {}
  }

  function dispatch(eventName, fullPayload) {
    fullPayload.event_name = eventName;
    emitDispatchTelemetry("queued", eventName);
    try {
      document.dispatchEvent(new CustomEvent("pma:analytics", { detail: { event: eventName, payload: fullPayload }, bubbles: true }));
    } catch (e) {
      emitDispatchTelemetry("failed", eventName, e);
      return;
    }
    try {
      if (typeof window.dataLayer !== "undefined" && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, pma: fullPayload });
      }
    } catch (e) {
      emitDispatchTelemetry("failed", eventName, e);
    }
    emitDispatchTelemetry("sent", eventName);
  }

  function dispatchHarnessOnly(eventName, fullPayload) {
    fullPayload.event_name = eventName;
    try {
      document.dispatchEvent(new CustomEvent("pma:analytics", { detail: { event: eventName, payload: fullPayload }, bubbles: true }));
    } catch {}
  }

  function track(eventName, payload, options) {
    if (!eventName || typeof eventName !== "string") return;
    if (!isCanonicalEventName(eventName)) {
      emitDispatchTelemetry("blocked_noncanonical", eventName);
      return;
    }
    var opts = options || {};
    var consentClass = opts.consent_class || defaultConsentClass;
    var dedupeKey = opts.dedupe_key;
    var oncePerPage = opts.once_per_page;

    var common = resolveCommonPayload();
    var key = dedupeKey || eventName + (oncePerPage ? ":" + (window.location.pathname || "") : "") + (payload && payload.resource_handle ? ":" + payload.resource_handle : "");
    if (isDedupeKeySeen(key)) return;

    var merged = {};
    for (var k in common) merged[k] = common[k];
    var sanitized = sanitizePayload(payload || {});
    for (var p in sanitized) merged[p] = sanitized[p];

    if (!isConsentGranted(consentClass, common.consent_state)) {
      emitDispatchTelemetry("blocked_consent", eventName);
      if (allowUnknownConsent()) {
        merged.debug_capture_only = true;
        dispatchHarnessOnly(eventName, merged);
      }
      return;
    }

    dispatch(eventName, merged);
  }

  function firePageView() {
    var ctx = getContext();
    var payload = {};
    if (ctx.resource_type) payload.resource_type = ctx.resource_type;
    if (ctx.resource_handle) payload.resource_handle = String(ctx.resource_handle);
    if (ctx.resource_id) payload.resource_id = String(ctx.resource_id);
    track("pma_page_view", payload, { once_per_page: true, consent_class: "analytics" });
    if (ctx.resource_type === "collection" && ctx.resource_handle) {
      var collPayload = { collection_handle: String(ctx.resource_handle) };
      if (ctx.collection_title) collPayload.collection_title = String(ctx.collection_title).trim().slice(0, TRUNCATE_LEN);
      track("pma_collection_view", collPayload, { once_per_page: true, consent_class: "analytics" });
    }
    if (ctx.resource_type === "product" && ctx.resource_handle) {
      var pPayload = {
        product_id: String(ctx.resource_id || ""),
        product_handle: String(ctx.resource_handle),
        is_price_locked: !document.body.classList.contains("is-member-active")
      };
      if (ctx.product_variant_id) pPayload.variant_id = String(ctx.product_variant_id);
      if (ctx.product_producer_handle) pPayload.producer_handle = String(ctx.product_producer_handle);
      if (ctx.product_available !== undefined) pPayload.inventory_state = ctx.product_available ? "in_stock" : "out_of_stock";
      track("pma_product_view", pPayload, { once_per_page: true, consent_class: "analytics" });
    }
    if (ctx.page_type === "producer_profile" && ctx.resource_handle) {
      var prodPayload = { producer_handle: String(ctx.resource_handle) };
      if (ctx.producer_name) prodPayload.producer_name = String(ctx.producer_name).trim().slice(0, TRUNCATE_LEN);
      track("pma_producer_view", prodPayload, { once_per_page: true, consent_class: "analytics" });
    }
    if (ctx.page_type === "producer_directory") {
      track("pma_producer_directory_view", {}, { once_per_page: true, consent_class: "analytics" });
    }
    if (ctx.page_type === "join") {
      track("pma_join_view", { join_surface: "page" }, { once_per_page: true, consent_class: "analytics" });
    }

    // APA-UX-041 / GH #6813: dedicated pending-state views (canonical pma_* names).
    var approvalState = getApprovalState();
    var membershipUi = getMembershipStateUi();
    var path = (typeof window !== "undefined" && window.location && window.location.pathname) || "";
    if (approvalState === "pending") {
      track(
        "pma_pending_state_view",
        {
          pending_surface: ctx.page_type || "page",
          membership_state: membershipUi || "pending_approval",
          approval_state: approvalState
        },
        { once_per_page: true, consent_class: "analytics" }
      );
    }
    var isApplicationStatusSurface =
      ctx.page_type === "join" ||
      ctx.page_type === "member_dashboard" ||
      ctx.page_type === "account" ||
      path.indexOf("/pages/member-dashboard") === 0 ||
      path.indexOf("/account") === 0;
    if (
      approvalState === "pending" &&
      isApplicationStatusSurface
    ) {
      track(
        "pma_application_pending_view",
        {
          pending_surface: ctx.page_type || "page",
          membership_state: membershipUi || "pending_approval",
          approval_state: approvalState,
          status_path: String(path).slice(0, TRUNCATE_LEN)
        },
        { once_per_page: true, consent_class: "analytics" }
      );
    }
  }

  function isMobileJourney() {
    return getDeviceType() === "mobile";
  }

  function pathFromHref(href) {
    if (!href) return "";
    try {
      var url = new URL(String(href), window.location.origin);
      return url.pathname || "/";
    } catch {
      return String(href).split("?")[0].split("#")[0].slice(0, TRUNCATE_LEN);
    }
  }

  function sectionHandle(el) {
    try {
      var section = el && el.closest && el.closest("[data-section-handle]");
      return section ? truncate(section.getAttribute("data-section-handle") || "") : "";
    } catch {
      return "";
    }
  }

  function classifyCta(el) {
    var raw = truncate(el && el.getAttribute && el.getAttribute("data-pma-cta"));
    var href = (el && el.getAttribute && el.getAttribute("href")) || "";
    var text = truncate(el && el.textContent);
    if (raw) return raw;
    if (el && el.getAttribute && el.getAttribute("data-pma-nav-priority") === "help") {
      if (href.indexOf("/pages/contact") !== -1) return "drawer_help_contact";
      if (href.indexOf("/pages/support") !== -1) return "drawer_help_support";
      return "drawer_help";
    }
    if (/apply|membership|join/i.test(text)) return "join";
    if (/contact/i.test(text)) return "contact";
    if (/support|help/i.test(text)) return "support";
    if (/shop|browse|products/i.test(text)) return "browse_products";
    return "cta";
  }

  function classifySurface(el) {
    try {
      if (!el) return "unknown";
      var explicit = el.getAttribute && el.getAttribute("data-pma-cta-surface");
      if (explicit) return truncate(explicit);
      var navArea = el.getAttribute && el.getAttribute("data-pma-nav-area");
      if (navArea) return truncate(navArea);
      if (el.closest(".pma-nav-drawer")) return "mobile_drawer";
      if (el.closest(".pma-header")) return "header";
      if (el.closest(".pma-footer")) return "footer";
      if (el.closest("form")) return "form";
      return sectionHandle(el) || "content";
    } catch {
      return "unknown";
    }
  }

  function classifyForm(form) {
    try {
      if (!form) return "unknown";
      if (form.matches("[data-pma-newsletter-form], .pma-newsletter-form")) return "newsletter";
      if (form.matches("[data-pma-join-form]")) return "membership_application";
      if (form.matches("[data-support-form], [data_support_form], [data-pma-support-form], .pma-support-ticket-form, .pma-support-contact-form")) return "support";
      if (form.matches("[data-pma-form], .pma-form")) return "contact";
      return "form";
    } catch {
      return "unknown";
    }
  }

  function formStats(form) {
    var fields = [];
    try {
      fields = Array.prototype.slice.call(form.querySelectorAll("input, select, textarea")).filter(function (field) {
        var type = String(field.type || "").toLowerCase();
        if (type === "hidden" || type === "submit" || type === "button" || type === "reset") return false;
        if (field.name === "company") return false;
        if (field.closest && field.closest(".pma-honeypot")) return false;
        return true;
      });
    } catch {}
    var completed = 0;
    fields.forEach(function (field) {
      try {
        if ((field.type === "checkbox" || field.type === "radio") && field.checked) completed += 1;
        else if (String(field.value || "").trim() !== "") completed += 1;
      } catch {}
    });
    return { total: fields.length, completed: completed };
  }

  function formId(form) {
    try {
      return truncate(form.getAttribute("id") || form.getAttribute("data-pma-form-id") || classifyForm(form));
    } catch {
      return "form";
    }
  }

  function initMobileCtaTracking() {
    document.addEventListener("click", function (event) {
      if (!isMobileJourney()) return;
      var target = event.target && event.target.closest && event.target.closest(
        "[data-pma-cta], a[data-pma-nav-priority='help'], button[data-pma-nav-priority='help']"
      );
      if (!target) return;
      var href = (target.getAttribute && target.getAttribute("href")) || "";
      var payload = {
        cta_id: classifyCta(target),
        cta_surface: classifySurface(target),
        origin_page_type: getContext().page_type || "page",
        destination: pathFromHref(href),
        link_text: truncate(target.textContent || "")
      };
      var resourceHandle = getContext().resource_handle;
      if (resourceHandle) payload.origin_resource_handle = String(resourceHandle);
      track("pma_mobile_cta_click", payload, { consent_class: "analytics" });
    }, true);
  }

  function initMobileScrollTracking() {
    if (!isMobileJourney()) return;
    var fired = {};
    var thresholds = [25, 50, 75, 90];
    var ticking = false;
    function check() {
      ticking = false;
      var doc = document.documentElement || document.body;
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var viewportHeight = window.innerHeight || doc.clientHeight || 0;
      var scrollHeight = Math.max(doc.scrollHeight || 0, document.body ? document.body.scrollHeight || 0 : 0);
      var max = Math.max(1, scrollHeight - viewportHeight);
      var pct = Math.min(100, Math.round((scrollTop / max) * 100));
      thresholds.forEach(function (threshold) {
        if (pct >= threshold && !fired[threshold]) {
          fired[threshold] = true;
          track("pma_mobile_scroll_depth", {
            depth_percent: threshold,
            max_scroll_px: Math.round(max),
            viewport_height: Math.round(viewportHeight),
            document_height: Math.round(scrollHeight)
          }, { consent_class: "analytics", dedupe_key: "pma_mobile_scroll_depth:" + window.location.pathname + ":" + threshold });
        }
      });
    }
    window.addEventListener("scroll", function () {
      if (ticking) return;
      ticking = true;
      setTimeout(check, 150);
    }, { passive: true });
    setTimeout(check, 1200);
  }

  function initCriticalCtaTiming() {
    if (!isMobileJourney() || typeof IntersectionObserver === "undefined") return;
    var selector = "[data-pma-cta='home_hero_browse'], [data-pma-cta='join'], [data-pma-cta='support-ticket'], [data-pma-cta='support-contextual'], .pma-newsletter-form button[type='submit']";
    var cta = document.querySelector(selector);
    if (!cta) return;
    var start = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        var now = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
        track("pma_mobile_critical_cta_view", {
          cta_id: classifyCta(cta),
          cta_surface: classifySurface(cta),
          time_to_cta_ms: Math.max(0, Math.round(now - start))
        }, { once_per_page: true, consent_class: "analytics" });
      });
    }, { threshold: 0.5 });
    observer.observe(cta);
  }

  function initMobileFormTracking() {
    var started = {};
    document.addEventListener("focusin", function (event) {
      if (!isMobileJourney()) return;
      var form = event.target && event.target.closest && event.target.closest("form");
      if (!form) return;
      var id = formId(form);
      if (started[id]) return;
      started[id] = true;
      var stats = formStats(form);
      track("pma_mobile_form_start", {
        form_id: id,
        form_type: classifyForm(form),
        field_count_total: stats.total
      }, { consent_class: "analytics" });
    }, true);
    document.addEventListener("submit", function (event) {
      if (!isMobileJourney()) return;
      var form = event.target;
      if (!form || !formStats) return;
      var stats = formStats(form);
      track("pma_mobile_form_submit_attempt", {
        form_id: formId(form),
        form_type: classifyForm(form),
        field_count_total: stats.total,
        field_count_completed: stats.completed,
        has_required_missing: Boolean(form.querySelector(":invalid"))
      }, { consent_class: "analytics" });
    }, true);
    document.addEventListener("pma:newsletter:submitted", function (event) {
      if (!isMobileJourney()) return;
      var detail = event && event.detail ? event.detail : {};
      track("pma_mobile_form_result", {
        form_id: "newsletter",
        form_type: "newsletter",
        result: "success",
        source: truncate(detail.source || "unknown")
      }, { consent_class: "analytics" });
    });
  }

  function initCoreWebVitalsTracking() {
    if (typeof PerformanceObserver === "undefined") return;
    var sent = {};
    function sendMetric(metricName, value, rating) {
      if (sent[metricName]) return;
      sent[metricName] = true;
      track("pma_core_web_vital", {
        metric_name: metricName,
        metric_value: Math.round(Number(value) || 0),
        metric_rating: rating || "unknown",
        metric_source: "performance_observer"
      }, { consent_class: "analytics", dedupe_key: "pma_core_web_vital:" + window.location.pathname + ":" + metricName });
    }
    function rating(metricName, value) {
      if (metricName === "LCP") return value <= 2500 ? "good" : value <= 4000 ? "needs_improvement" : "poor";
      if (metricName === "CLS") return value <= 0.1 ? "good" : value <= 0.25 ? "needs_improvement" : "poor";
      if (metricName === "INP") return value <= 200 ? "good" : value <= 500 ? "needs_improvement" : "poor";
      return "unknown";
    }
    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        var last = entries[entries.length - 1];
        if (last) sendMetric("LCP", last.startTime || last.renderTime || last.loadTime || 0, rating("LCP", last.startTime || 0));
      }).observe({ type: "largest-contentful-paint", buffered: true });
    } catch {}
    try {
      var cls = 0;
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (!entry.hadRecentInput) cls += entry.value || 0;
        });
      }).observe({ type: "layout-shift", buffered: true });
      window.addEventListener("pagehide", function () {
        sendMetric("CLS", Math.round(cls * 1000) / 1000, rating("CLS", cls));
      }, { once: true });
    } catch {}
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          var duration = entry.duration || 0;
          if (duration) sendMetric("INP", duration, rating("INP", duration));
        });
      }).observe({ type: "event", buffered: true, durationThreshold: 40 });
    } catch {}
  }

  function initMobileJourneyTracking() {
    initMobileCtaTracking();
    initMobileScrollTracking();
    initCriticalCtaTiming();
    initMobileFormTracking();
    initCoreWebVitalsTracking();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      firePageView();
      initMobileJourneyTracking();
    });
  } else {
    firePageView();
    initMobileJourneyTracking();
  }

  if (typeof window.PMAAnalytics === "undefined") {
    // firePageView exposed for harness/QA re-fire after persona class injection (GH #6813).
    window.PMAAnalytics = { track: track, firePageView: firePageView };
  } else {
    try {
      window.PMAAnalytics.firePageView = firePageView;
    } catch {}
  }
})();
